import { useBreakpoint } from "@avenue-8/sidekick-ui";
import { ChatWidgetRenderer } from "@cma/chat-widgets/ChatWidgetRenderer";
import { useThreadAutoInteraction } from "./hooks/useThreadAutoInteraction";
import { Fade } from "@mui/material";
import {
  buildGetMessagesKey,
  useGetMessages,
} from "@queries/chat/useGetMessages";
import { appEventEmitter } from "@shared/events/app-event-emitter";
import { useChatWidgetLogic } from "@shared/hooks/useChatWidgetLogic";
import { useIntercomIntegration } from "@shared/services/intercom/useIntercomIntegration";
import { sortByCreatedAt } from "@shared/utils/sortBy";
import { useAuthStore } from "@store/auth.store";
import { useUIStore } from "@store/ui.store";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router";
import { useAttachFile } from "../../queries/chat/useAttachFile";
import {
  buildMessageMetadataKey,
  ChatMessageArtifact,
  PENDING_THREAD_KEY,
  useMessagesStore,
} from "../../store/messages.store";
import { MessageSuggestionModel } from "./chat-helpers";
import { AppNavbar } from "./components/AppNavbar";
import { ChatExploreView } from "./components/ChatExploreView";
import {
  ChatFocusView,
  type FocusLeadSignal,
} from "./components/ChatFocusView";
import { ChatFooter } from "./components/ChatFooter";
import { ChatNewChatView } from "./components/ChatNewChatView";
import { ChatThreadView } from "./components/ChatThreadView";
import { Messages } from "./components/Messages";
import { SplitScreen } from "./components/SplitScreen";
import { StackedScreen } from "./components/StackedScreen";
import { useCancelLoadingMessages } from "./hooks/useCancelLoadingMessages";
import { useMessageStreamHandler } from "./hooks/useMessageStreamHandler";
import { useOpenLastChatWidget } from "./hooks/useOpenLastChatWidget";
import { useScrollToLastMessage } from "./hooks/useScrollToLastMessage";
import { Container, type ChatLayoutView } from "./styles";
import { useChatLogic } from "./useChatLogic";

type ChatShellView = "chat" | "explore" | "focus";

type Props = {
  currentThread: string | undefined;
  navigateToThread: (id: string | null) => void;
  /**
   * Shell tab: "chat" is /new + threads; "explore" is the Explore surface; "focus" reserved.
   */
  view?: ChatShellView;
};

export const Chat = (props: Props) => {
  const { currentThread, view = "chat" } = props;
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const { handleSendMessage, isSendingMessage, promptRefId, setPromptRefId } =
    useChatLogic(props);
  const user = useAuthStore((state) => state.user);
  const isLaptopDown = useBreakpoint("laptop");

  const messageInputText = useUIStore((state) => state.messageInputText);
  const messagesMap = useMessagesStore((state) => state.messagesMap);
  const messagesMetadataMap = useMessagesStore(
    (state) => state.messagesMetadataMap,
  );
  const isCreatingThread = useMessagesStore((state) => state.isCreatingThread);
  const navigate = useNavigate();
  const {
    chatWidget,
    chatWidgetDrawerState,
    setArtifactAsChatWidget,
    setChatWidget,
    toggleChatWidgetDrawer,
    markChatWidgetStale,
    setChatWidgetContext,
    resetChatWidget,
  } = useChatWidgetLogic();

  const intercom = useIntercomIntegration();

  useCancelLoadingMessages({ threadId: currentThread });

  const activeView: ChatLayoutView = currentThread
    ? "chat"
    : view === "chat"
      ? "new-chat"
      : view;

  const messages = useMemo(() => {
    if (currentThread) {
      const messages = messagesMap.get(currentThread);
      return messages?.length ? sortByCreatedAt(messages) : null;
    }
    if (isCreatingThread) {
      const pending = messagesMap.get(PENDING_THREAD_KEY);
      return pending?.length ? sortByCreatedAt(pending) : null;
    }
    return null;
  }, [currentThread, messagesMap, isCreatingThread]);

  const getMessageMetadata = (threadId: string, messageId: string) => {
    const metadata = messagesMetadataMap.get(
      buildMessageMetadataKey(threadId, messageId),
    );
    return metadata;
  };

  const attachFileMutation = useAttachFile();
  const queryClient = useQueryClient();

  const messageStreamListener = useMessageStreamHandler({
    promptRefId,
    currentThread,
    user,
    onStreamEnd: () => {
      setPromptRefId(null);
      queryClient.invalidateQueries({
        queryKey: buildGetMessagesKey({ threadId: currentThread || "" }),
      });
    },
    onStreamError: () => {
      setPromptRefId(null);
    },
  });

  useOpenLastChatWidget({
    messages: messages ?? [],
    currentThread,
  });

  const isUploadingFile = attachFileMutation.isPending;
  const isReceivingMessageResponse = messageStreamListener.isReceivingResponse;

  const messagesQuery = useGetMessages({
    threadId: currentThread,
    onError: () => {
      navigate("/");
    },
    onSuccess: () => {
      markChatWidgetStale();
    },
  });

  const handleAttachFile = useCallback(
    async (file: File) => {
      return attachFileMutation.mutateAsync({
        file,
      });
    },
    [attachFileMutation],
  );

  const setMessageInputTextValue = useUIStore(
    (state) => state.setMessageInputTextValue,
  );
  const messageInputFile = useUIStore((state) => state.messageInputFile);
  const messageForm = useUIStore((state) => state.messageForm);

  const handleSuggestionClick = useCallback(
    (suggestion: MessageSuggestionModel) => {
      const { autoSubmit = true } = suggestion;

      if (suggestion.hidden) {
        handleSendMessage({
          hidden: suggestion.hidden,
          message: suggestion.prompt,
        });
        return;
      }

      setMessageInputTextValue(suggestion.prompt);

      if (suggestion.type === "file") {
        messageInputFile?.click();
        return;
      }

      messageInputText?.focus();

      if (autoSubmit) {
        messageForm?.requestSubmit();
        return;
      }
    },
    [
      handleSendMessage,
      messageForm,
      messageInputFile,
      messageInputText,
      setMessageInputTextValue,
    ],
  );

  const handleThreadSuggestionClick = useCallback(
    ({ prompt }: { prompt: string }) => {
      appEventEmitter.emit({
        eventType: "sidekick-suggestions-select-click",
      });
      handleSuggestionClick({
        id: Date.now().toString(),
        prompt,
        hidden: false,
        type: "text",
      });
    },
    [handleSuggestionClick],
  );

  const handleFocusLeadClick = useCallback(
    (lead: FocusLeadSignal) => {
      // Using router state instead of query param - no redirect needed if URL changes, feature still being validated
      const FOCUS_LEAD_AUTO_OPEN = true;
      navigate(`/contacts/${lead.contactId}`, {
        state: FOCUS_LEAD_AUTO_OPEN ? { openLeadId: lead.id } : undefined,
      });
    },
    [navigate],
  );

  const numberOfMessages = (messages ?? []).length;
  const isShowingThreadContent = !!currentThread || numberOfMessages > 0;

  const {
    threadAutoInteractionConfig: currentThreadAutoInteractionConfig,
    isMessageBarOverlayHidden,
    hasMoreAutoInteractions,
    nextMessageIndex,
    handleFinishAutoTyping,
  } = useThreadAutoInteraction({
    currentThread,
    numberOfMessages,
    isSendingMessage,
    isReceivingMessageResponse,
  });

  const isLoadingWidget = useMemo(
    () =>
      isSendingMessage ||
      messageStreamListener.state === "awaiting" ||
      messageStreamListener.state === "executing-tool-call" ||
      messageStreamListener.state === "reasoning",
    [isSendingMessage, messageStreamListener.state],
  );

  const handleArtifactClick = useCallback(
    (artifact: ChatMessageArtifact) => {
      setArtifactAsChatWidget(artifact);
      toggleChatWidgetDrawer(isLaptopDown ? "half-expanded" : "expanded");
    },
    [setArtifactAsChatWidget, toggleChatWidgetDrawer, isLaptopDown],
  );

  const isLoadingMessages = messagesQuery?.isLoading && !messages;

  const { scrolledThreadId } = useScrollToLastMessage({
    messagesContainerRef,
    scrollOnLoad: true,
    currentThread,
    numberOfMessages,
    isLoadingMessages,
  });
  const isThreadViewReady =
    isCreatingThread || scrolledThreadId === currentThread;

  const isSidebarOpenDesktop = useUIStore(
    (state) => state.isSidebarOpenDesktop,
  );

  if (isLaptopDown) {
    return (
      <StackedScreen
        onDrawerStateChange={chatWidget ? toggleChatWidgetDrawer : () => {}}
        drawerState={chatWidget ? chatWidgetDrawerState : "expanded"}
        showDragHandle={!!chatWidget}
        hasWidget={!!chatWidget}
        drawerContent={
          <Container $stackedLayout={!!chatWidget} $view={activeView}>
            {!chatWidget && (isShowingThreadContent || view !== "explore") && (
              <AppNavbar />
            )}
            {currentThread ? (
              <ChatThreadView
                currentThread={currentThread}
                isReady={isThreadViewReady}
                isSendingMessage={isSendingMessage}
                isStreamAwaiting={messageStreamListener.state === "awaiting"}
                hasPendingBotMessage={
                  messagesQuery?.hasPendingBotMessage ?? false
                }
                isReconnecting={messageStreamListener.state === "reconnecting"}
                isReceivingMessageResponse={
                  messageStreamListener.isReceivingResponse
                }
                isLoadingMessages={isLoadingMessages}
                messages={messages ?? []}
                getMessageMetadata={getMessageMetadata}
                handleThreadSuggestionClick={handleThreadSuggestionClick}
                handleArtifactClick={handleArtifactClick}
                hasNextPage={messagesQuery?.hasNextPage}
                fetchNextPage={messagesQuery?.fetchNextPage}
                isFetchingNextPage={messagesQuery?.isFetchingNextPage}
                messagesContainerRef={messagesContainerRef}
                hasMoreAutoInteractions={hasMoreAutoInteractions}
              />
            ) : numberOfMessages > 0 ? (
              <Messages
                isReady
                isSendingMessage
                isReconnecting={false}
                isReceivingMessageResponse={false}
                currentThread={undefined}
                isLoadingMessages={false}
                messages={messages ?? []}
                getMessageMetadata={getMessageMetadata}
                handleThreadSuggestionClick={handleThreadSuggestionClick}
                handleArtifactClick={handleArtifactClick}
                messagesContainerRef={messagesContainerRef}
              />
            ) : chatWidget?.temporary ? null : view === "explore" ? (
              <ChatExploreView
                isSendingMessage={isSendingMessage}
                onSelectItem={(item) =>
                  handleSuggestionClick({
                    id: item.id,
                    prompt: item.prompt,
                    type: item.type,
                    autoSubmit: item.autoSubmit,
                  })
                }
                onBack={() => navigate("/")}
              />
            ) : view === "focus" ? (
              <ChatFocusView
                isSendingMessage={isSendingMessage}
                onPromptItemClick={(item) =>
                  handleSuggestionClick({
                    id: item.id,
                    prompt: item.prompt,
                    type: item.type,
                    autoSubmit: true,
                  })
                }
                onLeadClick={handleFocusLeadClick}
              />
            ) : (
              <ChatNewChatView
                isSendingMessage={isSendingMessage}
                onItemClick={(item) =>
                  handleSuggestionClick({
                    id: item.id,
                    prompt: item.prompt,
                    type: item.type,
                    autoSubmit: true,
                  })
                }
              />
            )}
          </Container>
        }
        footerContent={(isDragging) => (
          <ChatFooter
            containerRef={messagesContainerRef}
            isScrollReady={scrolledThreadId === currentThread}
            showScrollToBottom
            scrollToBottomHidden={
              isDragging ||
              (!!chatWidget && chatWidgetDrawerState === "collapsed")
            }
            stackedLayout={!!chatWidget}
            currentThread={currentThread}
            onSendMessage={handleSendMessage}
            onAttachFile={handleAttachFile}
            numberOfMessages={numberOfMessages}
            isSendingMessage={isSendingMessage}
            isReceivingMessageResponse={
              messageStreamListener.isReceivingResponse
            }
            isUploadingFile={isUploadingFile}
            disabled={
              messagesQuery?.isLoading ||
              intercom.isIntercomOpen ||
              user.subscriptionStatus !== "active"
            }
            isSubmitDisabled={messageStreamListener.isReceivingResponse}
            threadAutoInteractionConfig={currentThreadAutoInteractionConfig}
            hasMoreAutoInteractions={hasMoreAutoInteractions}
            nextMessageIndex={nextMessageIndex}
            isOverlayHidden={isMessageBarOverlayHidden}
            handleFinishAutoTyping={handleFinishAutoTyping}
          />
        )}
        mainContent={
          <Fade in={!!chatWidget} unmountOnExit>
            <ChatWidgetRenderer
              widget={chatWidget}
              isLoading={isLoadingWidget}
              setChatWidgetContext={setChatWidgetContext}
              setChatWidget={setChatWidget}
              resetChatWidget={resetChatWidget}
            />
          </Fade>
        }
      />
    );
  }

  return (
    <SplitScreen
      open={chatWidgetDrawerState !== "collapsed"}
      onOpen={() => toggleChatWidgetDrawer("expanded")}
      onClose={() => toggleChatWidgetDrawer("collapsed")}
      drawerWidth="50vw"
      mainContent={
        <Container
          $isSidebarOpenDesktop={isSidebarOpenDesktop}
          $view={activeView}
        >
          {(isShowingThreadContent || view !== "explore") && <AppNavbar />}
          {currentThread ? (
            <ChatThreadView
              currentThread={currentThread}
              isReady={isThreadViewReady}
              isSendingMessage={isSendingMessage}
              isStreamAwaiting={messageStreamListener.state === "awaiting"}
              hasPendingBotMessage={
                messagesQuery?.hasPendingBotMessage ?? false
              }
              isReconnecting={messageStreamListener.state === "reconnecting"}
              isReceivingMessageResponse={
                messageStreamListener.isReceivingResponse
              }
              isLoadingMessages={isLoadingMessages}
              messages={messages ?? []}
              getMessageMetadata={getMessageMetadata}
              handleThreadSuggestionClick={handleThreadSuggestionClick}
              handleArtifactClick={handleArtifactClick}
              hasNextPage={messagesQuery?.hasNextPage}
              fetchNextPage={messagesQuery?.fetchNextPage}
              isFetchingNextPage={messagesQuery?.isFetchingNextPage}
              messagesContainerRef={messagesContainerRef}
              hasMoreAutoInteractions={hasMoreAutoInteractions}
            />
          ) : numberOfMessages > 0 ? (
            <Messages
              isReady
              isSendingMessage
              isReconnecting={false}
              isReceivingMessageResponse={false}
              currentThread={undefined}
              isLoadingMessages={false}
              messages={messages ?? []}
              getMessageMetadata={getMessageMetadata}
              handleThreadSuggestionClick={handleThreadSuggestionClick}
              handleArtifactClick={handleArtifactClick}
              messagesContainerRef={messagesContainerRef}
            />
          ) : chatWidget?.temporary ? null : view === "explore" ? (
            <ChatExploreView
              isSendingMessage={isSendingMessage}
              onSelectItem={(item) =>
                handleSuggestionClick({
                  id: item.id,
                  prompt: item.prompt,
                  type: item.type,
                  autoSubmit: item.autoSubmit,
                })
              }
              onBack={() => navigate("/")}
            />
          ) : view === "focus" ? (
            <ChatFocusView
              isSendingMessage={isSendingMessage}
              onPromptItemClick={(item) =>
                handleSuggestionClick({
                  id: item.id,
                  prompt: item.prompt,
                  type: item.type,
                  autoSubmit: true,
                })
              }
              onLeadClick={handleFocusLeadClick}
            />
          ) : (
            <ChatNewChatView
              isSendingMessage={isSendingMessage}
              onItemClick={(item) =>
                handleSuggestionClick({
                  id: item.id,
                  prompt: item.prompt,
                  type: item.type,
                  autoSubmit: true,
                })
              }
            />
          )}
          <ChatFooter
            containerRef={messagesContainerRef}
            isScrollReady={scrolledThreadId === currentThread}
            showScrollToBottom={isShowingThreadContent}
            currentThread={currentThread}
            onSendMessage={handleSendMessage}
            onAttachFile={handleAttachFile}
            numberOfMessages={numberOfMessages}
            isSendingMessage={isSendingMessage}
            isReceivingMessageResponse={
              messageStreamListener.isReceivingResponse
            }
            isUploadingFile={isUploadingFile}
            disabled={
              messagesQuery?.isLoading ||
              intercom.isIntercomOpen ||
              user.subscriptionStatus !== "active"
            }
            isSubmitDisabled={messageStreamListener.isReceivingResponse}
            threadAutoInteractionConfig={currentThreadAutoInteractionConfig}
            hasMoreAutoInteractions={hasMoreAutoInteractions}
            nextMessageIndex={nextMessageIndex}
            isOverlayHidden={isMessageBarOverlayHidden}
            handleFinishAutoTyping={handleFinishAutoTyping}
          />
        </Container>
      }
      showDrawer={!!chatWidget}
      title={chatWidget?.title}
      splitContent={
        <ChatWidgetRenderer
          widget={chatWidget}
          isLoading={isLoadingWidget}
          setChatWidgetContext={setChatWidgetContext}
          setChatWidget={setChatWidget}
          resetChatWidget={resetChatWidget}
        />
      }
    />
  );
};
